import React, { useCallback, useEffect } from 'react';
import CanvasPopup, { useCanvasPopup } from '../../services/editor/CanvasPopup';
import CanvasPopupBaseComponent from '../../lib/popup/CanvasPopupBaseComponent';
import { getCanvasEntityDOMId } from '../../services/editor/ui';
import { EditorService } from '../../services/editor';
import { CanvasEntityElement } from '../../services/editor/CanvasElement';
import {
	DefinitionEntityState,
	EntityType,
	EntryPointEntityState,
	PassThroughCallableEntityState
} from '@elyx-code/project-logic-tree';
import './node-description-popup.css';

export interface IUseNodeDescriptionPopupProps {
	entity:
		| PassThroughCallableEntityState
		| EntryPointEntityState
		| DefinitionEntityState;
	canvasElement: CanvasEntityElement;
	project: EditorService;
}

export function useNodeDescriptionPopup(props: IUseNodeDescriptionPopupProps) {
	const entity = props?.entity;
	const canvasElement = props?.canvasElement;
	const project = props?.project;

	const isEntityValid = !!entity && !entity.deleted;

	let description = '';

	if (entity && !entity.deleted) {
		if (
			EntityType.VariableInstance === entity.type ||
			EntityType.Condition === entity.type ||
			EntityType.Operation === entity.type ||
			EntityType.FunctionCall === entity.type ||
			EntityType.InternalCall === entity.type ||
			EntityType.Loop === entity.type
		) {
			description = entity.declaration?.description || '';
		} else {
			description = entity.description || '';
		}
	}

	const hasDescription =
		isEntityValid && typeof description === 'string' && description.trim().length > 0;

	const memoizedPopupBody = useCallback(
		(popup: CanvasPopup) => (
			<CanvasPopupBaseComponent
				id={`canvas-node-description-popup--${entity?.id}--body`}
				containerClasses={['node-description-popup', 'tooltip']}
			>
				<div className="node-description-popup__content">
					<div className="node-description-popup__header">
						<i className="fa-solid fa-circle-info node-description-popup__icon" />
						<span className="node-description-popup__title">
							Description
						</span>
					</div>
					<div className="node-description-popup__body">
						{description}
					</div>
				</div>
			</CanvasPopupBaseComponent>
		),
		[entity?.id, description]
	);

	const popup = useCanvasPopup(
		{
			editor: project,
			id: `canvas-node-description-popup--${entity?.id || 'none'}`,
			anchorQuerySelector: entity ? `#${getCanvasEntityDOMId(entity)}` : '',
			placement: 'top',
			parentCanvasObject: canvasElement?.draggableObject,
			arrowClasses: ['node-description-popup-arrow'],
			arrowContainerSelector: `#canvas-node-description-popup--${entity?.id || 'none'}--body`
		},
		memoizedPopupBody,
		[entity?.id, description, project]
	);

	if (popup) {
		popup.onBeforeShow(() => {
			return hasDescription;
		});
	}

	useEffect(() => {
		if (hasDescription && popup && popup.initialized && isEntityValid) {
			popup.setupHover({
				openDelay: 800,
				closeDelay: 300
			});
		}
	}, [popup, hasDescription, popup?.initialized, isEntityValid]);

	if (!isEntityValid) {
		return null;
	}

	return popup;
}

export default useNodeDescriptionPopup;
